/* David — Clinq's financial + pitch deck advisor. Replaces the previous
   Clinq AI floating chat widget. David keeps a single long-running
   conversation per founder inside an external Supabase table so the
   chats are shared with the founder's other Clinq surfaces.

   Mount anywhere the floating "Ask David" bubble should appear:
       <David />
*/
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import {
  X, PaperPlaneTilt, ChatCircleText, Trash, ArrowsClockwise, ArrowRight,
} from "@phosphor-icons/react";
import SnapshotInterview, { getSnapshotState, CompletedBadge } from "@/components/SnapshotInterview";

const C = {
  panel: "#0A2826", panelDeep: "#06201E", teal: "#0A8B85",
  tealSoft: "#7BD3CE", orange: "#F59E0B",
};

/* Iter108 — one-tap suggested-action executor. David appends structured
   action hints to some replies; we render them as chips. Each `kind`
   here MUST exactly match the whitelist in /app/backend/david_router.py
   (`ALLOWED_ACTION_KINDS`) — unknown kinds are silently dropped. */
const ACTION_HANDLERS = {
  open_dashboard:              (nav) => nav("/dashboard"),
  open_new_pitch_deck:         (nav) => nav("/pitch/new"),
  open_new_financial_model:    (nav) => nav("/fm/new"),
  open_new_valuation:          (nav) => nav("/valuation/new"),
  open_mgmt_accounts:          (nav) => nav("/mgmt/new"),
  open_credits:                (nav) => nav("/credits"),
  book_advisor_session:        (nav) => nav("/dashboard?advisor=general"),
  clear_david_history: async (_nav, ctx) => { await ctx.onClearHistory(); },
};

const AVATAR_SRC = "/david-avatar.png";

const STARTER_PROMPTS = [
  "Sanity-check my runway and burn — where's the risk?",
  "Score my pitch deck against the 5 pillars",
  "Which assumptions in my model would a Seed VC probe first?",
  "Rewrite my one-liner — make it sharper",
];

export default function David({ autoOpenForSnapshot = false, fmId = null, varianceAlert = null }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [supabaseOn, setSupabaseOn] = useState(true);
  const [snapState, setSnapState] = useState(null);
  const [snapLoading, setSnapLoading] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Fetch the snapshot state once on mount so the nudge + auto-open
  // logic below can be snapshot-aware before the panel ever opens.
  useEffect(() => {
    getSnapshotState().then((s) => setSnapState(s)).catch(() => {});
  }, []);

  // Iter110: on open, re-fetch the founder's snapshot state — this
  // determines whether David is GATED (interview UI) or unlocked.
  useEffect(() => {
    if (!open) return;
    setSnapLoading(true);
    getSnapshotState()
      .then((s) => setSnapState(s))
      .finally(() => setSnapLoading(false));
  }, [open]);

  const gated = !!snapState && !snapState.completed && !snapState.missing_supabase && !snapState.error;
  const snapError = snapState?.error || null;
  // "Their business info is still missing" — drives auto-open + the
  // faster, snapshot-aware nudge.
  const snapIncomplete = gated;

  // One-time auto-open: the FIRST time a new founder lands on the
  // dashboard with an incomplete snapshot, open David so he can run
  // the interview. Dismissible; never fires again on this device.
  useEffect(() => {
    if (!autoOpenForSnapshot || open || !snapIncomplete) return;
    if (typeof window === "undefined") return;
    if (localStorage.getItem("david-snapshot-autoopen") === "1") return;
    localStorage.setItem("david-snapshot-autoopen", "1");
    setOpen(true);
  }, [autoOpenForSnapshot, open, snapIncomplete]);

  const reopenSnapshot = async () => {
    try {
      const { data } = await api.post("/david/snapshot/reopen");
      setSnapState(data);
    } catch { toast.error("Could not reopen snapshot"); }
  };

  /* Iter113 — friendly one-time-per-session nudge that pops out of
     the floating bubble ~45s after the founder lands, if they haven't
     opened David yet. Contextual copy per route. Dismisses on click,
     on the ✕, on opening David, or after 12s visible. `sessionStorage`
     tracks that we've shown it so we don't nag on every route change. */
  const [nudge, setNudge] = useState(false);
  useEffect(() => {
    if (open) return undefined;
    if (typeof window === "undefined") return undefined;
    if (sessionStorage.getItem("david-nudge-shown") === "1") return undefined;
    // Snapshot-aware timing: incomplete snapshot → nudge at 10s with
    // setup copy; otherwise the regular 45s contextual nudge.
    const delay = snapIncomplete ? 10000 : 45000;
    const showT = setTimeout(() => {
      // guard: don't nudge if David got opened during the wait
      if (sessionStorage.getItem("david-nudge-shown") === "1") return;
      setNudge(true);
      sessionStorage.setItem("david-nudge-shown", "1");
    }, delay);
    return () => clearTimeout(showT);
  }, [open, snapIncomplete]);

  useEffect(() => {
    if (!nudge) return undefined;
    const hideT = setTimeout(() => setNudge(false), 12000);
    return () => clearTimeout(hideT);
  }, [nudge]);

  const nudgeCopy = useMemo(() => {
    if (snapIncomplete) {
      const a = snapState?.progress?.answered || 0;
      const t = snapState?.progress?.total || 8;
      if (a > 0) return `You're ${a}/${t} through setup — ${t - a} more quick answer${t - a === 1 ? "" : "s"} unlocks advice that fits your business.`;
      return "Quick 60-second setup — tell me about your business so my advice actually fits.";
    }
    const p = typeof window === "undefined" ? "" : window.location.pathname;
    if (p.startsWith("/pitch"))      return "Hitting a wall on this deck? I've reviewed thousands — just ask.";
    if (p.startsWith("/builder"))    return "Financial models are brutal. Ping me if something looks off.";
    if (p.startsWith("/mgmt"))       return "Numbers not tying up? I can spot the issue in a minute.";
    if (p.startsWith("/valuation"))  return "Not sure which valuation method fits? I can walk you through it.";
    if (p.startsWith("/credits"))    return "Choosing a pack? I'll tell you what each one really unlocks.";
    if (p.startsWith("/dashboard"))  return "Just a heads up — I'm here if you get stuck. Fundraising advice on tap.";
    return "Friendly reminder — I'm here if you have any questions.";
  }, [nudge, snapIncomplete]);

  const openFromNudge = () => { setNudge(false); setOpen(true); };

  /* Proactive FM walkthrough — the FIRST time this founder is on a Financial
     Model page (snapshot complete), David opens himself and offers a
     step-by-step tour. Memory = Supabase chat history via the status
     endpoint, so once offered it never auto-fires again on any device. */
  useEffect(() => {
    if (!fmId || !snapState || gated || snapState.missing_supabase || snapState.error) return;
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem("david-fm-walkthrough-checked") === "1") return;
    sessionStorage.setItem("david-fm-walkthrough-checked", "1");
    api.get("/david/fm-walkthrough/status")
      .then(({ data }) => {
        if (data?.offered) return null;
        return api.post("/david/fm-walkthrough/offer");
      })
      .then((res) => {
        if (!res?.data?.message) return;
        setMessages((m) => [...m, normaliseRow(res.data.message)]);
        setNudge(false);
        setOpen(true);
      })
      .catch(() => {});
  }, [fmId, snapState, gated]);

  // Lazy-load history from Supabase the first time the panel opens
  useEffect(() => {
    if (!open || loaded) return;
    api.get("/david/history")
      .then(({ data }) => {
        const rows = (data?.messages || []).map((r) => ({
          id: r.id ?? `srv-${Math.random().toString(36).slice(2, 9)}`,
          role: r.role,
          content: r.content,
          ts: r.created_at,
        }));
        setMessages(rows);
      })
      .catch((e) => {
        const detail = e?.response?.data?.detail || "";
        if (detail.toLowerCase().includes("supabase")) {
          setSupabaseOn(false);
        }
      })
      .finally(() => setLoaded(true));
  }, [open, loaded]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async (preset) => {
    const body = (preset ?? text).trim();
    if (!body || busy) return;
    setBusy(true);
    setText("");
    const tempUserId = `temp-user-${Date.now()}`;
    const tempAssistantId = `temp-assistant-${Date.now()}`;
    setMessages((m) => [
      ...m,
      { id: tempUserId, role: "user", content: body, ts: new Date().toISOString() },
      { id: tempAssistantId, role: "assistant", content: "", ts: new Date().toISOString(), streaming: true },
    ]);

    const tok = localStorage.getItem("session_token");
    let userMsgFinal = null;
    let assistantFinal = null;
    let assembled = "";
    let receivedAny = false;

    try {
      const resp = await fetch(`${API}/david/chat-stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify({ message: body, ...(fmId ? { fm_id: fmId } : {}) }),
      });
      if (!resp.ok || !resp.body) {
        const errText = await resp.text().catch(() => "");
        throw new Error(errText || `Stream failed (${resp.status})`);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf("\n\n")) !== -1) {
          const chunk = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          let evt = "message";
          let data = "";
          for (const line of chunk.split("\n")) {
            if (line.startsWith("event:")) evt = line.slice(6).trim();
            else if (line.startsWith("data:")) data += line.slice(5).trim();
          }
          if (!data) continue;
          let parsed;
          try { parsed = JSON.parse(data); } catch { continue; }
          if (evt === "user_message") {
            userMsgFinal = normaliseRow(parsed);
          } else if (evt === "delta") {
            receivedAny = true;
            assembled += parsed.content || "";
            setMessages((m) => m.map((x) => x.id === tempAssistantId ? { ...x, content: assembled } : x));
          } else if (evt === "content_final") {
            // Iter108: server has stripped the ```suggested_actions```
            // block from the reply — swap the streamed text for the
            // clean prose so the founder never sees the raw fence.
            assembled = parsed.content || assembled;
            setMessages((m) => m.map((x) => x.id === tempAssistantId ? { ...x, content: assembled } : x));
          } else if (evt === "done") {
            assistantFinal = normaliseRow(parsed);
          } else if (evt === "error") {
            throw new Error(parsed.detail || "Stream error");
          }
        }
      }

      if (!assistantFinal) throw new Error("Stream ended without a final reply");
      setMessages((m) => {
        const without = m.filter((x) => x.id !== tempUserId && x.id !== tempAssistantId);
        return [
          ...without,
          userMsgFinal || { id: tempUserId, role: "user", content: body, ts: new Date().toISOString() },
          assistantFinal,
        ];
      });
    } catch (e) {
      const detail = (e && e.message) || "David is having a moment — try again.";
      toast.error(detail);
      setMessages((m) => {
        const filtered = m.filter((x) => x.id !== tempUserId && !(x.id === tempAssistantId && !receivedAny));
        if (receivedAny) {
          return filtered.map((x) => x.id === tempAssistantId ? { ...x, streaming: false } : x);
        }
        return filtered;
      });
      if (!receivedAny) setText(body);
      if (detail.toLowerCase().includes("supabase")) setSupabaseOn(false);
    } finally {
      setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const clearThread = async () => {
    if (!window.confirm("Start a fresh conversation? Your previous chat with David will be cleared.")) return;
    try {
      await api.delete("/david/history");
      setMessages([]);
      toast.success("Fresh start with David");
    } catch { toast.error("Could not clear"); }
  };

  /* Iter108: fire a David-suggested action. Unknown kinds are silently
     dropped (defence-in-depth alongside the backend whitelist). Actions
     that navigate close the panel so the founder lands on the target
     page. */
  const runAction = async (action) => {
    if (!action || typeof action !== "object") return;
    const handler = ACTION_HANDLERS[action.kind];
    if (!handler) {
      toast.error("David suggested an action I don't know how to run yet.");
      return;
    }
    try {
      await handler(navigate, { onClearHistory: async () => {
        try { await api.delete("/david/history"); setMessages([]); toast.success("Fresh start with David"); }
        catch { toast.error("Could not clear"); }
      }});
      // Navigation actions get a small confirmation + the panel closes
      // so the founder lands on the new page without a stale side-panel.
      if (action.kind.startsWith("open_") || action.kind === "book_advisor_session") setOpen(false);
    } catch (e) {
      toast.error(e?.message || "Action failed");
    }
  };

  return (
    <>
      {/* Iter113: nudge speech bubble — floats above the Ask David
          button. Only shows once per browser session. */}
      {!open && nudge && (
        <div
          data-testid="david-nudge"
          className="fixed bottom-24 right-6 z-40 max-w-[280px] rounded-2xl px-4 py-3 shadow-2xl"
          style={{
            backgroundColor: "#0A2826",
            border: `1px solid ${C.teal}`,
            boxShadow: "0 12px 40px rgba(10, 139, 133, 0.45)",
            animation: "davidNudgePop 320ms ease-out",
          }}>
          <button
            data-testid="david-nudge-close"
            onClick={() => setNudge(false)}
            aria-label="Dismiss nudge"
            className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full text-white/70 hover:text-white text-[10px] font-bold grid place-items-center"
            style={{ backgroundColor: "#06201E", border: "1px solid rgba(123,211,206,0.35)" }}>
            ✕
          </button>
          <div className="flex items-start gap-2.5">
            <img src={AVATAR_SRC} alt="David"
              className="w-8 h-8 rounded-full object-cover shrink-0"
              style={{ border: `1.5px solid ${C.teal}` }} />
            <button
              data-testid="david-nudge-open"
              onClick={openFromNudge}
              className="text-left"
              style={{ background: "transparent", border: 0, padding: 0 }}>
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold mb-0.5"
                style={{ color: C.orange }}>
                David
              </div>
              <div className="text-[12px] leading-snug text-white/90">
                {nudgeCopy}
              </div>
              <div className="text-[10px] mt-1.5 font-bold" style={{ color: C.tealSoft }}>
                Tap to chat →
              </div>
            </button>
          </div>
          {/* Little tail pointing down at the bubble */}
          <div className="absolute -bottom-2 right-8 w-4 h-4"
            style={{
              backgroundColor: "#0A2826",
              borderRight: `1px solid ${C.teal}`,
              borderBottom: `1px solid ${C.teal}`,
              transform: "rotate(45deg)",
            }} />
        </div>
      )}

      {!open && (
        <button onClick={() => setOpen(true)} data-testid="david-open"
          aria-label="Open David — your funder-ready documents advisor"
          className="fixed bottom-6 right-6 z-40 rounded-full pl-2.5 pr-5 py-2.5 inline-flex items-center gap-2.5 font-semibold text-sm shadow-2xl transition-transform hover:scale-105"
          style={{
            backgroundColor: C.teal, color: "#fff",
            boxShadow: "0 12px 32px rgba(10, 139, 133, 0.45), 0 0 0 1px rgba(123, 211, 206, 0.3) inset",
          }}>
          <span className="relative inline-block">
            <img src={AVATAR_SRC} alt="David"
              className="w-8 h-8 rounded-full object-cover"
              style={{ border: "2px solid rgba(255,255,255,0.35)" }}
              data-testid="david-avatar-badge" />
            <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: C.orange, border: "1.5px solid #fff" }} />
          </span>
          Ask David
        </button>
      )}

      {open && (
        <div className="fixed inset-y-0 right-0 z-40 w-full sm:w-[440px] flex flex-col text-white shadow-2xl"
          data-testid="david-panel"
          style={{ backgroundColor: C.panelDeep, fontFamily: "Manrope, sans-serif", borderLeft: "1px solid rgba(123, 211, 206, 0.18)" }}>
          <header className="px-5 py-4 flex items-center justify-between"
            style={{ backgroundColor: C.panel, borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
            <div className="flex items-center gap-3">
              <img src={AVATAR_SRC} alt="David"
                className="w-10 h-10 rounded-full object-cover"
                style={{ border: `2px solid ${C.teal}` }}
                data-testid="david-avatar-header" />
              <div>
                <div className="font-extrabold text-sm tracking-tight flex items-center gap-1.5">
                  David
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: C.orange }} />
                </div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 font-bold">
                  Funder-ready documents advisor
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {messages.length > 0 && (
                <button onClick={clearThread} title="Clear conversation" data-testid="david-clear"
                  className="text-white/40 hover:text-white p-2">
                  <Trash size={14} />
                </button>
              )}
              <button onClick={() => setOpen(false)} title="Close" data-testid="david-close"
                className="text-white/55 hover:text-white p-2">
                <X size={18} />
              </button>
            </div>
          </header>

          {!supabaseOn && (
            <div className="px-4 py-2.5 text-[11px]" data-testid="david-supabase-warning"
              style={{ backgroundColor: "rgba(245,158,11,0.10)", color: C.orange, borderBottom: "1px solid rgba(245,158,11,0.25)" }}>
              David&apos;s memory is offline — this session won&apos;t be saved. Set <b>SUPABASE_URL</b> and <b>SUPABASE_KEY</b> in the backend .env.
            </div>
          )}

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-5" data-testid="david-messages">
            {snapError && (
              <div className="p-3 rounded-md text-[11px] mb-3" data-testid="david-snapshot-error"
                style={{ backgroundColor: "rgba(245,158,11,0.10)", border: "1px solid rgba(245,158,11,0.30)", color: C.orange }}>
                <b>Setup pending:</b> {snapError} An admin needs to run the DDL in the Supabase SQL editor.
              </div>
            )}
            {snapState?.completed && !snapState?.missing_supabase && (
              <div className="mb-3">
                <CompletedBadge onReopen={reopenSnapshot} />
              </div>
            )}
            {snapLoading ? (
              <div className="text-white/40 text-sm text-center pt-8">Loading…</div>
            ) : gated ? (
              <SnapshotInterview
                state={snapState}
                onStateChange={setSnapState}
                onCompleted={() => { /* toast shown inside; nothing else to do */ }}
              />
            ) : !loaded ? (
              <div className="text-white/40 text-sm text-center pt-8">Loading conversation…</div>
            ) : messages.length === 0 ? (
              <EmptyState onPick={send} fmId={fmId} varianceAlert={varianceAlert} />
            ) : (
              <div className="space-y-4">
                {messages.map((m) => (
                  <Bubble
                    key={m.id}
                    role={m.role}
                    content={m.content}
                    streaming={m.streaming}
                    suggestedActions={m.suggestedActions}
                    onAction={(a) => runAction(a)}
                    onClose={() => setOpen(false)}
                  />
                ))}
                {!busy && messages.length > 0
                  && messages[messages.length - 1].role === "assistant"
                  && (messages[messages.length - 1].content || "").includes("walk you through this page step-by-step") && (
                  <div className="flex flex-wrap gap-2" data-testid="david-walkthrough-chips">
                    <button onClick={() => send("Yes please — walk me through it step by step.")}
                      data-testid="david-walkthrough-yes"
                      className="text-[13px] font-bold px-4 py-2.5 rounded-full transition-colors"
                      style={{ backgroundColor: C.teal, color: "#fff" }}>
                      Yes — walk me through it
                    </button>
                    <button onClick={() => send("No thanks — I'll explore on my own for now.")}
                      data-testid="david-walkthrough-no"
                      className="text-[13px] px-4 py-2.5 rounded-full transition-colors text-white/60 hover:text-white"
                      style={{ border: "1px solid rgba(255,255,255,0.15)" }}>
                      I'll explore on my own
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Iter110: hide the free-form input while gated — the founder
              must finish the snapshot interview above before David
              accepts any other prompt. */}
          {!gated && !snapError && (
          <form onSubmit={(e) => { e.preventDefault(); send(); }}
            className="p-3" style={{ borderTop: "1px solid rgba(255,255,255,0.08)", backgroundColor: C.panel }}>
            <div className="flex items-end gap-2">
              <textarea ref={inputRef} rows={1} value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Ask David about your model, deck, or funder strategy…"
                data-testid="david-input"
                disabled={busy}
                className="flex-1 px-3 py-2.5 rounded-md text-sm resize-none focus:outline-none focus:border-[#0A8B85]"
                style={{ backgroundColor: "rgba(0,0,0,0.45)", border: "1px solid rgba(255,255,255,0.15)", color: "#fff", maxHeight: "120px" }} />
              <button type="submit" disabled={busy || !text.trim()} data-testid="david-send"
                className="px-3 py-2.5 rounded-md font-semibold disabled:opacity-50"
                style={{ backgroundColor: C.teal, color: "#fff" }}>
                {busy ? <ArrowsClockwise size={14} className="animate-spin" /> : <PaperPlaneTilt size={14} weight="bold" />}
              </button>
            </div>
            <div className="text-[10px] text-white/35 mt-2 px-1">
              Powered by Claude Sonnet 4.5 · memory synced across your Clinq apps via Supabase.
            </div>
          </form>
          )}
        </div>
      )}
    </>
  );
}

/* -------------------- helpers -------------------- */

function normaliseRow(r) {
  return {
    id: r?.id ?? `srv-${Math.random().toString(36).slice(2, 9)}`,
    role: r?.role || "assistant",
    content: r?.content || "",
    ts: r?.created_at || r?.ts || new Date().toISOString(),
    suggestedActions: Array.isArray(r?.suggested_actions) ? r.suggested_actions : [],
  };
}

function EmptyState({ onPick, fmId, varianceAlert }) {
  const prompts = fmId
    ? ["Coach me on this model — which assumptions look weak and which checks am I failing?", ...STARTER_PROMPTS]
    : STARTER_PROMPTS;
  const pctOfPlan = varianceAlert ? Math.round((varianceAlert.ytd_pct_of_forecast || 0) * 100) : null;
  return (
    <div data-testid="david-empty">
      <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] font-bold mb-4"
        style={{ color: C.orange }}>
        <ChatCircleText size={12} weight="fill" /> Advisor · methodology-aware
      </div>
      <div className="flex items-start gap-3 mb-5">
        <img src={AVATAR_SRC} alt="David" className="w-14 h-14 rounded-full object-cover"
          style={{ border: `2px solid ${C.teal}` }} />
        <div>
          <h3 className="text-xl font-extrabold leading-tight tracking-tight"
            style={{ letterSpacing: "-0.02em" }}>
            Hey, I&apos;m <span style={{ color: C.tealSoft }}>David</span>.
          </h3>
          <p className="text-white/60 text-[13px] mt-1 leading-relaxed">
            Your co-founder for financial models, pitch decks, and funder strategy. Ask me anything — I remember our previous chats.
          </p>
        </div>
      </div>
      <div className="grid gap-2">
        {pctOfPlan != null && (
          <button
            onClick={() => onPick(`I'm tracking at ${pctOfPlan}% of my forecast — help me work out what's driving the miss and what I should adjust.`)}
            data-testid="david-variance-chip"
            className="text-left text-[13px] px-4 py-3 rounded-lg transition-colors"
            style={{ backgroundColor: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.45)", color: "#FCA5A5" }}>
            <span className="font-bold">You're tracking at {pctOfPlan}% of plan.</span> Ask me what to adjust →
          </button>
        )}
        {prompts.map((p) => (
          <button key={p} onClick={() => onPick(p)}
            data-testid={`david-starter-${p.slice(0, 20).replace(/\s+/g, '-').toLowerCase()}`}
            className="text-left text-sm px-3 py-2.5 rounded-md hover:bg-white/5 transition-colors"
            style={{ backgroundColor: "rgba(10, 139, 133, 0.08)", border: "1px solid rgba(10, 139, 133, 0.18)", color: "rgba(255,255,255,0.85)" }}>
            {p}
          </button>
        ))}
      </div>
    </div>
  );
}

function Bubble({ role, content, streaming, suggestedActions, onAction }) {
  const isUser = role === "user";
  const showDots = !isUser && streaming && !content;
  const actions = !isUser && !streaming && Array.isArray(suggestedActions) ? suggestedActions : [];
  return (
    <div className={`flex flex-col ${isUser ? "items-end" : "items-start"} gap-1.5`}
      data-testid={`david-bubble-${role}`}>
      <div className={`flex ${isUser ? "justify-end" : "justify-start"} gap-2 w-full`}>
        {!isUser && (
          <img src={AVATAR_SRC} alt="David" className="w-7 h-7 rounded-full object-cover shrink-0 mt-0.5"
            style={{ border: `1.5px solid ${C.teal}` }} />
        )}
        <div className="max-w-[82%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap"
          style={isUser ? {
            backgroundColor: C.teal, color: "#fff", borderBottomRightRadius: "4px",
          } : {
            backgroundColor: "rgba(255,255,255,0.05)",
            border: "1px solid rgba(123, 211, 206, 0.12)",
            color: "rgba(255,255,255,0.92)",
            borderBottomLeftRadius: "4px",
          }}>
          {showDots ? (
            <span className="inline-flex items-center gap-1.5 py-0.5" data-testid="david-typing">
              <Dot delay="0s" /> <Dot delay="0.15s" /> <Dot delay="0.3s" />
            </span>
          ) : (
            <>
              {content}
              {streaming && !isUser && content && (
                <span className="inline-block w-1 h-3.5 ml-0.5 align-middle animate-pulse"
                  style={{ backgroundColor: C.tealSoft }} />
              )}
            </>
          )}
        </div>
      </div>
      {actions.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pl-9" data-testid="david-actions">
          {actions.map((a, i) => (
            <button
              key={`${a.kind}-${i}`}
              onClick={() => onAction?.(a)}
              data-testid={`david-action-${a.kind}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold transition-all hover:-translate-y-px"
              style={{
                backgroundColor: "rgba(245, 158, 11, 0.12)",
                border: `1px solid ${C.orange}`,
                color: C.orange,
              }}>
              {a.label}
              <ArrowRight size={10} weight="bold" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Dot({ delay }) {
  return (
    <span className="w-1.5 h-1.5 rounded-full inline-block animate-bounce"
      style={{ backgroundColor: C.tealSoft, animationDelay: delay }} />
  );
}
